/* DymoOnline — supplier stock gauge on product cards
 *
 * Replaces the theme's own inventory line on product cards ("5 Last Items",
 * "Available") with a miniature version of the fuel gauge used by the stock-on-hand
 * widget on the product page, with the status word underneath.
 *
 * Loaded on every page from a loader section in sections/header-group.json, so it
 * covers collection pages, search results, the header's predictive-search dropdown,
 * the home page sliders and product recommendations.
 *
 * Why: the theme's line reports SHOPIFY's local inventory, which is routinely a
 * handful of units while the supplier network is holding hundreds. It reads as
 * scarcity that isn't real. This reads the same national supplier feed the PDP
 * widget and the Stock Finder use, so the card and the product page agree.
 *
 * Data source
 *   megaofficesupplies.com.au/cdn/shop/files/itf-soh.json — shared across the group.
 *   Shape: {v, built, s:{SKU:[QLD,NSW,VIC,WA,SA]}}. ~3.1MB decoded / ~615KB over the
 *   wire (brotli), immutable, cached a year, busted by the version in itf-stamp.json.
 *   Served with Access-Control-Allow-Origin: *.
 *
 * Cost control
 *   1. Deferred — on a page that already has cards, nothing is requested until the
 *      grid is within 300px of the viewport (or 2.5s has passed), so it never
 *      competes with first paint. On a page with no cards the feed is not touched
 *      at all until cards appear (e.g. the user opens the search dropdown).
 *   2. Parsed in a Web Worker, so the 3.1MB JSON.parse never blocks the main thread.
 *      The worker keeps the full map in ITS memory and returns only the SKUs on
 *      screen, so the page holds a few hundred bytes rather than megabytes. Falls
 *      back to a main-thread load if Workers are unavailable.
 *
 * Finding the SKU
 *   Collection cards print it in .card__sku. Search result and dropdown cards do NOT
 *   — the theme's main-search section never passes show_sku to the product-card
 *   snippet (the collection grid does). Rather than fork a 21KB Liquid section,
 *   missing SKUs are resolved from the card's product handle via
 *   /products/<handle>.js, six at a time, and cached in sessionStorage so it happens
 *   at most once per product per session.
 *
 * Thresholds, colours, segment geometry and needle angles are copied verbatim from
 * pdp-stock.js so the card and the product page can never disagree.
 *
 * A SKU missing from the feed is left alone — the theme's own text stays.
 */
(function () {
  "use strict";

  if (window.__dymoCardGauge) return;
  window.__dymoCardGauge = 1;

  var FEED_ORIGIN = "https://megaofficesupplies.com.au";
  var STATES = ["QLD", "NSW", "VIC", "WA", "SA"];
  var CACHE_KEY = "dymo-cg-sku";
  var CONCURRENCY = 6;

  // Verbatim from pdp-stock.js. First tier whose `max` the quantity does not exceed wins.
  var TIERS = [
    { max: 0,        color: "#9F0C0C", lit: 0, ang: 172, label: "Restocking" },
    { max: 10,       color: "#CE5F13", lit: 1, ang: 140, label: "Last Few" },
    { max: 50,       color: "#FAB219", lit: 3, ang: 90,  label: "Low Stock" },
    { max: Infinity, color: "#319F0C", lit: 5, ang: 8,   label: "In Stock" }
  ];

  var SEGMENTS = [
    "M12.0 60.3 A50 50 0 0 1 20.2 32.6 L36.9 43.6 A30 30 0 0 0 32.0 60.2 Z",
    "M21.8 30.3 A50 50 0 0 1 44.9 13.0 L51.7 31.8 A30 30 0 0 0 37.9 42.2 Z",
    "M47.5 12.1 A50 50 0 0 1 76.5 12.1 L70.7 31.3 A30 30 0 0 0 53.3 31.3 Z",
    "M79.1 13.0 A50 50 0 0 1 102.2 30.3 L86.1 42.2 A30 30 0 0 0 72.3 31.8 Z",
    "M103.8 32.6 A50 50 0 0 1 112.0 60.3 L92.0 60.2 A30 30 0 0 0 87.1 43.6 Z"
  ];
  var EMPTY = "#C6C6C6";
  var CSS =
    ".dymo-cg{display:inline-flex;flex-direction:column;align-items:flex-start;gap:1px;line-height:1}" +
    ".dymo-cg__g{width:44px;height:26px;display:block}" +
    ".dymo-cg__lb{font-size:10.5px;font-weight:700;letter-spacing:.01em}";

  var WORKER_SRC = [
    "var MAP=null,ORIGIN='',ready=null;",
    "function load(){",
    "  return fetch(ORIGIN+'/cdn/shop/files/itf-stamp.json').then(function(r){return r.json();})",
    "    .catch(function(){return {};})",
    "    .then(function(st){",
    "      var q=st&&st.v?('?v='+encodeURIComponent(st.v)):'';",
    "      return fetch(ORIGIN+'/cdn/shop/files/itf-soh.json'+q);",
    "    })",
    "    .then(function(r){ if(!r.ok) throw new Error('feed '+r.status); return r.json(); })",
    "    .then(function(j){ MAP=j.s||{}; return {built:j.built||'',v:j.v||''}; });",
    "}",
    "self.onmessage=function(e){",
    "  var d=e.data||{};",
    "  if(d.origin) ORIGIN=d.origin;",
    "  if(!ready) ready=load();",
    "  ready.then(function(meta){",
    "    var out={},i,k,skus=d.skus||[];",
    "    for(i=0;i<skus.length;i++){ k=skus[i]; if(MAP[k]) out[k]=MAP[k]; }",
    "    self.postMessage({id:d.id,ok:true,meta:meta,data:out});",
    "  }).catch(function(err){ self.postMessage({id:d.id,ok:false,error:String(err)}); });",
    "};"
  ].join("\n");

  /* ---- drawing ---- */

  function tierFor(qty) {
    for (var i = 0; i < TIERS.length; i++) if (qty <= TIERS[i].max) return TIERS[i];
    return TIERS[TIERS.length - 1];
  }

  function gauge(t) {
    var paths = "";
    for (var i = 0; i < 5; i++) {
      paths += '<path d="' + SEGMENTS[i] + '" fill="' + (i < t.lit ? t.color : EMPTY) + '"/>';
    }
    var a = t.ang * Math.PI / 180;
    var x = (62 + 43 * Math.cos(a)).toFixed(1);
    var y = (60 - 43 * Math.sin(a)).toFixed(1);
    return '<svg class="dymo-cg__g" viewBox="0 0 124 74" aria-hidden="true" focusable="false">' +
      paths +
      '<line x1="62" y1="60" x2="' + x + '" y2="' + y + '" stroke="' + t.color + '" stroke-width="5" stroke-linecap="round"/>' +
      '<circle cx="62" cy="60" r="8.5" fill="' + t.color + '"/></svg>';
  }

  function injectCss() {
    if (document.getElementById("dymo-cg-css")) return;
    var el = document.createElement("style");
    el.id = "dymo-cg-css";
    el.textContent = CSS;
    document.head.appendChild(el);
  }

  // The theme only renders .product__inventory when inventory tracking is on, so build
  // the host if it is absent. That also means tracking can be switched off per product
  // without losing the gauge.
  function hostFor(card) {
    var host = card.querySelector(".product__inventory-inner");
    if (host) return host;
    var anchor = card.querySelector(".card__collection-title") || card.querySelector(".card__sku");
    if (!anchor || !anchor.parentElement) return null;
    var wrap = document.createElement("div");
    wrap.className = "product__inventory";
    host = document.createElement("div");
    host.className = "product__inventory-inner";
    wrap.appendChild(host);
    anchor.parentElement.appendChild(wrap);
    return host;
  }

  function paint(card, arr, meta) {
    var total = 0, i;
    for (i = 0; i < arr.length; i++) total += arr[i] || 0;
    var t = tierFor(total);
    var host = hostFor(card);
    if (!host) return;

    host.classList.remove("product__inventory-inner--error");
    host.innerHTML =
      '<span class="dymo-cg">' + gauge(t) +
      '<span class="dymo-cg__lb" style="color:' + t.color + '">' + t.label + '</span></span>';

    var detail = [];
    for (i = 0; i < STATES.length; i++) detail.push(STATES[i] + " " + (arr[i] || 0));
    host.setAttribute("title",
      "Supplier stock: " + total + " units nationally (" + detail.join(", ") + ")" +
      (meta && meta.built ? " — updated " + meta.built : ""));
  }

  /* ---- handle -> sku, for cards that do not print one ---- */

  var skuCache = (function () {
    try { return JSON.parse(sessionStorage.getItem(CACHE_KEY)) || {}; } catch (e) { return {}; }
  })();

  function saveCache() {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(skuCache)); } catch (e) { /* full or private */ }
  }

  function skuFromProduct(p) {
    if (!p || !p.variants || !p.variants.length) return "";
    for (var i = 0; i < p.variants.length; i++) {
      if (p.variants[i].available && p.variants[i].sku) return p.variants[i].sku;
    }
    return p.variants[0].sku || "";
  }

  function resolveHandles(handles, done) {
    var todo = [], i;
    for (i = 0; i < handles.length; i++) {
      if (!Object.prototype.hasOwnProperty.call(skuCache, handles[i])) todo.push(handles[i]);
    }
    if (!todo.length) { done(); return; }

    var next = 0, active = 0, finished = 0;

    function step() {
      while (active < CONCURRENCY && next < todo.length) {
        (function (handle) {
          active++;
          fetch("/products/" + encodeURIComponent(handle) + ".js")
            .then(function (r) { return r.ok ? r.json() : null; })
            .catch(function () { return null; })
            .then(function (p) {
              skuCache[handle] = skuFromProduct(p);   // "" is a valid answer: do not retry
              active--; finished++;
              if (finished === todo.length) { saveCache(); done(); } else step();
            });
        })(todo[next++]);
      }
    }
    step();
  }

  /* ---- feed access: worker first, main thread as a fallback ---- */

  var worker = null, workerDead = false, seq = 0, waiting = {};
  var mainMap = null, mainMeta = null, mainLoad = null;

  function ensureWorker() {
    if (worker || workerDead) return worker;
    try {
      var url = URL.createObjectURL(new Blob([WORKER_SRC], { type: "text/javascript" }));
      worker = new Worker(url);
      URL.revokeObjectURL(url);
      worker.onmessage = function (e) {
        var m = e.data || {}, cb = waiting[m.id];
        delete waiting[m.id];
        if (!cb) return;
        cb(m.ok ? m.data : null, m.meta);
      };
      worker.onerror = function () { workerDead = true; worker = null; };
    } catch (err) {
      workerDead = true;
      worker = null;
    }
    return worker;
  }

  function loadOnMain() {
    if (mainLoad) return mainLoad;
    mainLoad = fetch(FEED_ORIGIN + "/cdn/shop/files/itf-stamp.json")
      .then(function (r) { return r.json(); })
      .catch(function () { return {}; })
      .then(function (st) {
        var q = st && st.v ? "?v=" + encodeURIComponent(st.v) : "";
        return fetch(FEED_ORIGIN + "/cdn/shop/files/itf-soh.json" + q);
      })
      .then(function (r) { if (!r.ok) throw new Error("feed " + r.status); return r.json(); })
      .then(function (j) {
        mainMap = j.s || {};
        mainMeta = { built: j.built || "", v: j.v || "" };
      });
    return mainLoad;
  }

  function lookup(skus, cb) {
    var w = ensureWorker();
    if (w) {
      var id = ++seq;
      waiting[id] = function (data, meta) {
        if (data) cb(data, meta);
        else { workerDead = true; worker = null; lookup(skus, cb); }   // retry on main thread
      };
      w.postMessage({ id: id, origin: FEED_ORIGIN, skus: skus });
      return;
    }
    loadOnMain().then(function () {
      var out = {}, i;
      for (i = 0; i < skus.length; i++) if (mainMap[skus[i]]) out[skus[i]] = mainMap[skus[i]];
      cb(out, mainMeta);
    }).catch(function () { /* leave the theme's own text in place */ });
  }

  /* ---- run ---- */

  function handleOf(card) {
    var a = card.querySelector('a[href*="/products/"]');
    if (!a) return "";
    var m = (a.getAttribute("href") || "").match(/\/products\/([^/?#]+)/);
    return m ? m[1] : "";
  }

  function run() {
    var pending = [], handles = [], i;
    var all = document.querySelectorAll(".product-card");

    for (i = 0; i < all.length; i++) {
      var card = all[i];
      if (card.hasAttribute("data-dymo-cg")) continue;
      card.setAttribute("data-dymo-cg", "1");

      var el = card.querySelector(".card__sku");
      var sku = el ? el.textContent.replace(/SKU:?/i, "").trim() : "";
      if (sku) {
        pending.push({ el: card, sku: sku });
      } else {
        var h = handleOf(card);
        if (!h) continue;
        pending.push({ el: card, handle: h });
        handles.push(h);
      }
    }
    if (!pending.length) return;

    resolveHandles(handles, function () {
      var skus = [], seen = {}, j;
      for (j = 0; j < pending.length; j++) {
        if (!pending[j].sku) pending[j].sku = skuCache[pending[j].handle] || "";
        var s = pending[j].sku;
        if (s && !seen[s]) { seen[s] = 1; skus.push(s); }
      }
      if (!skus.length) return;

      lookup(skus, function (data, meta) {
        if (!data) return;
        injectCss();
        for (var k = 0; k < pending.length; k++) {
          var arr = data[pending[k].sku];
          if (arr) paint(pending[k].el, arr, meta);
        }
      });
    });
  }

  var started = false;

  function activate() {
    if (started) return;
    started = true;
    run();
  }

  function onMutate() {
    // Once running, repaint whatever filtering, sorting or pagination swapped in.
    if (started) { run(); return; }
    // Not running yet: a page that had no cards has just been given some — this is
    // the header's predictive-search dropdown. Start now rather than never.
    if (document.querySelector(".product-card")) activate();
  }

  function watch() {
    if (!window.MutationObserver) return;
    var t;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(onMutate, 200);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function boot() {
    watch();

    var first = document.querySelector(".product-card");
    if (!first) return;   // nothing to paint yet; the observer will start us if that changes

    if ("IntersectionObserver" in window) {
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) { io.disconnect(); activate(); return; }
        }
      }, { rootMargin: "300px" });
      io.observe(first);
    }
    setTimeout(activate, 2500);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
